import { cloneDeep } from 'lodash';
import { createId, type RegenerateIds } from '@shared/helpers/id.helpers';
import { omitUndefined } from '@shared/helpers/object.helpers';
import {
  DEFAULT_PROPOSAL_NAME,
  type IProposal,
} from '@shared/models/proposals.model';
import type { Baseline } from '@shared/models/project.model';

/**
 * Builds an {@link IProposal} with defaults for tests and fixtures.
 *
 * @param base - Partial proposal fields; `id` is optional and generated when missing or when {@link RegenerateIds} forces it.
 *   When appending via {@link addProposal}, `actionIds` are always cleared so the new row
 *   does not share {@link Baseline.actions} with another proposal (`resultsRecord`
 *   and other fields may still be seeded from a source row).
 * @param regenerateIds - How ids are generated; same semantics as {@link createElementOfType}. Regenerates by default.
 */
export const createProposal = (
  base: Partial<IProposal> = {},
  regenerateIds?: RegenerateIds,
): IProposal => {
  const cleaned = omitUndefined(base);
  const {
    actionIds: sourceActionIds,
    resultsRecord: sourceResultsRecord,
    ...rest
  } = cleaned;
  return {
    name: DEFAULT_PROPOSAL_NAME,
    colorIndex: 0,
    ...rest,
    actionIds: sourceActionIds !== undefined ? [...sourceActionIds] : [],
    resultsRecord:
      sourceResultsRecord !== undefined ? cloneDeep(sourceResultsRecord) : {},
    id: createId(cleaned, regenerateIds),
  };
};

/**
 * Enforces the project invariant that every building version owns at least one
 * proposal. Existing proposal rows are preserved by reference; an empty version
 * receives one default, action-free proposal seeded with the baseline results.
 *
 * The seed matters: {@link getResultsRecord} only falls back to
 * {@link Baseline.resultsRecord} when the proposal record is undefined, and an
 * action-free proposal has the baseline's numbers by definition. Leaving it `{}`
 * would render the backfilled proposal at zero until the next enrichment.
 */
export const ensureVersionHasProposal = (version: Baseline): Baseline => {
  if (version.proposals?.length) return version;
  return {
    ...version,
    proposals: [createProposal({ resultsRecord: version.resultsRecord })],
  };
};
